import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import type { StoryChapter } from '../../types/premium';
import './SolutionStoryChapters.css';

interface SolutionStoryChaptersProps {
  chapters: StoryChapter[];
}

const chapterViewport = { once: true, amount: 0.3 as const };

const SolutionStoryChapters: React.FC<SolutionStoryChaptersProps> = ({ chapters }) => {
  const prefersReducedMotion = useReducedMotion();

  return (
    <section className="solution-story-chapters">
      <div className="container">
        {chapters.map((chapter, index) => {
          const reversed = chapter.align === 'right' || index % 2 === 1;
          const stagger = index * 0.06;

          const visualInitial = prefersReducedMotion
            ? { opacity: 0 }
            : { opacity: 0, x: reversed ? 48 : -48, scale: 0.94 };
          const visualAnimate = prefersReducedMotion
            ? { opacity: 1 }
            : { opacity: 1, x: 0, scale: 1 };
          const visualTransition = prefersReducedMotion
            ? { delay: stagger, duration: 0.45 }
            : { delay: stagger, duration: 0.65, ease: [0.22, 1, 0.36, 1] as const };

          const copyInitial = prefersReducedMotion
            ? { opacity: 0 }
            : { opacity: 0, x: reversed ? -36 : 36, y: 16 };
          const copyAnimate = prefersReducedMotion
            ? { opacity: 1 }
            : { opacity: 1, x: 0, y: 0 };
          const copyTransition = prefersReducedMotion
            ? { delay: stagger + 0.08, duration: 0.45 }
            : { delay: stagger + 0.12, duration: 0.65, ease: [0.22, 1, 0.36, 1] as const };

          return (
            <div
              key={chapter.id}
              className={`solution-story-chapter ${reversed ? 'solution-story-chapter--reverse' : ''}`}
            >
              <motion.div
                className="solution-story-chapter__visual"
                viewport={chapterViewport}
                initial={visualInitial}
                whileInView={visualAnimate}
                transition={visualTransition}
                whileHover={prefersReducedMotion ? undefined : { scale: 1.02 }}
              >
                {chapter.image ? (
                  <img
                    src={chapter.image}
                    alt={chapter.imageAlt ?? chapter.title}
                    loading="lazy"
                  />
                ) : (
                  <div className="solution-story-chapter__visual-placeholder" aria-hidden="true">
                    <span>{chapter.eyebrow}</span>
                  </div>
                )}
              </motion.div>
              <motion.div
                className="solution-story-chapter__copy"
                viewport={chapterViewport}
                initial={copyInitial}
                whileInView={copyAnimate}
                transition={copyTransition}
              >
                <span className="solution-story-chapter__eyebrow">{chapter.eyebrow}</span>
                <h3>{chapter.title}</h3>
                <p>{chapter.body}</p>
                {chapter.quote ? <p className="solution-story-chapter__quote">{chapter.quote}</p> : null}
              </motion.div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default SolutionStoryChapters;
